import { lazy } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import ModuleRouteGuard from "../common/components/ModuleRouteGuard";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";

const InvoiceLayout = lazy(() => import("../common/components/InvoiceLayout"));
const EmployerNotes = lazy(() => import("../modules/Billing/Notes"));
const Settings = lazy(() => import("../modules/Billing/Settings"));
const Profile = lazy(() => import("../modules/Billing/Settings/Profile"));
const AllEmployees = lazy(
  () => import("../modules/EmployerManagement/All Employees"),
);
const EmployeeBasicDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/BasicsDetails"),
);
const BillingWrapper = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/BillingWrapper"),
);
const EmployeeCredits = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Credits"),
);
const EmployeeInvoices = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Invoices"),
);
const EmployeeMemberships = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships"),
);
const MembershipDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships/MembershipDetails"),
);
const EmployeeNotes = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notes"),
);
const EmployeeNotifications = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notifications"),
);
const EmployeePaymentOptions = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/PaymentOptions"),
);
const EmployersList = lazy(
  () => import("../modules/EmployerManagement/employers"),
);
const EmployerMain = lazy(
  () => import("../modules/EmployerManagement/employers/employerMain"),
);
const AccountDetails = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/AccountDetails"),
);
const EmployerDetails = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/Employees"),
);
const EmployerEmployeeMain = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/Employees/EmployeeMain"),
);
const EmployeeUploads = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads"),
);
const DetailedRows = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads/Tabs/DetailedRows"),
);
const ProcessedRows = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads/Tabs/ProcessedRows"),
);
const InvoiceSettings = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/InvoiceSettings"),
);
const OnlineSignup = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/OnlineSignUp"),
);
const NewSignupPage = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/OnlineSignUp/NewSignupPage/newSignupPage"),
);
const PaymentOption = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/PaymentOption"),
);
const Pricing = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/Pricing"),
);
const ViewPlan = lazy(
  () => import("../modules/EmployerManagement/Settings/Tabs/Plans/ViewPlan"),
);
const SignupWithPHP = lazy(
  () => import("./employer-management/employers/sign-up"),
);
const EmployerInvoices = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/Invoices"),
);
const EmployerInvoiceLayout = lazy(
  () => import("../common/components/InvoiceLayout/EmployerInvoiceLayout"),
);
const CoveragePlan = lazy(
  () => import("../modules/Admin/Membership/coverage-plan"),
);
const DocumentViewer = lazy(() => import("../modules/DocumentViewer"));
const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const BillingRoutes = (
  <Route path="/billing" element={<MainRoute />}>
    <Route
      path="coverage-plans/"
      element={
        <ModuleRouteGuard
          moduleType="employer-management"
          routePath="coverage-plans"
        >
          <CoveragePlan />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="settings"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="settings">
          <Settings />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="notifications" replace />} />
      <Route
        path="profile"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="profile">
            <Profile />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notifications">
            <>Notifications</>
          </ModuleRouteGuard>
        }
      />
    </Route>

    <Route
      path="all-employees"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="all-employees">
          <AllEmployees feature="Billing Patients" />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="employers"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <EmployersList feature="Billing Employer" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <EmployerMain />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="employees" replace />} />
      <Route
        path="invoices"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employers">
            <EmployerInvoices feature="Billing Employer Invoices" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="employees"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employers">
            <EmployerDetails feature="Billing Employees" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="account-details"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employers">
            <AccountDetails feature="EM Account Details" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="pricing"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="pricing">
            <Pricing feature="Billing Pricing" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="pricing/plans-details/:id"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="plans">
            <ViewPlan />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="online-signup"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employers">
            <OnlineSignup />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="payment-option"
        element={
          <ModuleRouteGuard
            moduleType="billing"
            routePath="employer-payment-option"
          >
            <PaymentOption feature="Billing Employer Payment Option" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="employee-uploads"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employers">
            <EmployeeUploads feature="EM Employee Uploads" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoice-settings"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="invoice-settings">
            <InvoiceSettings feature={"EM Invoice Settings"} />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notes"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notes">
            <EmployerNotes feature="Billing Employer Notes" />
          </ModuleRouteGuard>
        }
      />
      <Route path="invoices/view-document" element={<DocumentViewer />} />
    </Route>
    <Route
      path="all-employees/:id/invoice/:invoiceUuid/:mode?"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employee-invoices">
          <InvoiceLayout />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="all-employees/sign-up/:employeeUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="all-employees">
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="all-employees/sign-up"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="all-employees">
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/sign-up/:employeeUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/sign-up"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/new-signup-page"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <NewSignupPage />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/new-signup-page/:employerPageUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <NewSignupPage />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/employee-uploads/:uploadUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <ProcessedRows />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/employee-uploads/:uploadUuid/view-all"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <DetailedRows />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="/billing/employers/:employerUuid/employees/:employeeUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employers">
          <EmployerEmployeeMain />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="employee-details" replace />} />
      <Route
        path="employee-details"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="all-employees">
            <EmployeeBasicDetails feature="Billing Basic Details" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoices"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employee-invoices">
            <EmployeeInvoices feature="Billing Employee Invoices" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="credits"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="credits">
            <EmployeeCredits feature="Billing Credits" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="memberships"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="memberships">
            <EmployeeMemberships feature="Billing Memberships" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="payment-options"
        element={
          <ModuleRouteGuard
            moduleType="billing"
            routePath="employer-payment-option"
          >
            <EmployeePaymentOptions feature="Billing Employer Payment Option" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notes"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notes">
            <EmployeeNotes feature="Billing employee Notes" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notifications">
            <EmployeeNotifications feature="Billing employee Notifications" />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="employers/:employerUuid/employees/:employeeUuid/invoice/:invoiceUuid/:mode?"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employee-invoices">
          <InvoiceLayout />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/invoice/:invoiceUuid/:mode?"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="employee-invoices">
          <EmployerInvoiceLayout />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="all-employees/:employeeUuid"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="all-employees">
          <BillingWrapper />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="employee-details" replace />} />
      <Route
        path="employee-details"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="basic-details">
            <EmployeeBasicDetails feature="Billing Basic Details" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoices"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="employee-invoices">
            <EmployeeInvoices feature="Billing Employee Invoices" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="credits"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="credits">
            <EmployeeCredits feature="Billing Credits" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="memberships"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="memberships">
            <EmployeeMemberships feature="Billing Memberships" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="payment-options"
        element={
          <ModuleRouteGuard
            moduleType="billing"
            routePath="employee-payment-option"
          >
            <EmployeePaymentOptions feature="Billing Employee Payment Option" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notes"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notes">
            <EmployeeNotes feature="Billing employee Notes" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications"
        element={
          <ModuleRouteGuard moduleType="billing" routePath="notifications">
            <EmployeeNotifications feature="Billing employee Notifications" />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="all-employees/:employeeUuid/membership-details"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="memberships">
          <MembershipDetails feature="Billing Memberships" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employers/:employerUuid/employees/:employeeUuid/membership-details"
      element={
        <ModuleRouteGuard moduleType="billing" routePath="memberships">
          <MembershipDetails feature="Billing Memberships" />
        </ModuleRouteGuard>
      }
    />
  </Route>
);

export default BillingRoutes;
